import { useRef } from "react"
import { Button, Popover, PopoverTrigger, PopoverContent, PopoverBody, Portal, VStack, Icon, useColorModeValue, useDisclosure } from "@chakra-ui/react"
import { DownloadIcon, ChevronDownIcon } from "@chakra-ui/icons"
import { FaFileExcel } from "react-icons/fa"

/**
 * The "Export" button + report list used by Strike Rate, Ranging and Margin.
 *
 * Click to open; clicking a report (or anywhere else) closes it. The previous
 * per-page copies opened on hover and closed on mouse-leave, with a padding/
 * margin gap between the button and the list — crossing that gap fired the
 * leave and unmounted the list before it could be clicked, and a 5-second
 * timer closed it regardless. Sales keeps its own richer sectioned popover;
 * this is the plain single-list version built on the same Chakra Popover.
 *
 * items: [{ label, onClick, icon?, colorScheme? }]
 */
const ExportMenu = ({ items, isLoading = false, width = "240px" }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const triggerRef = useRef()
  const buttonBg = useColorModeValue("primary1", "darkPrimary1")
  const buttonHoverBg = useColorModeValue("primary2", "darkPrimary2")
  const popoverBg = useColorModeValue("white", "gray.800")
  const popoverBorderColor = useColorModeValue("gray.200", "gray.700")
  const popoverShadow = useColorModeValue("lg", "dark-lg")

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" autoFocus={false} initialFocusRef={triggerRef} closeOnBlur>
      <PopoverTrigger>
        <Button
          ref={triggerRef}
          leftIcon={<DownloadIcon />}
          rightIcon={<ChevronDownIcon />}
          bg={buttonBg}
          color="white"
          _hover={{ bg: buttonHoverBg }}
          _active={{ bg: buttonHoverBg }}
          isLoading={isLoading}
          loadingText="Exporting..."
          boxShadow="sm"
          size="sm"
        >
          Export
        </Button>
      </PopoverTrigger>
      <Portal>
        <PopoverContent width={width} bg={popoverBg} borderColor={popoverBorderColor} borderRadius="md" boxShadow={popoverShadow} _focus={{ outline: "none" }} zIndex={1400} rootProps={{ zIndex: 1400 }}>
          <PopoverBody p={2}>
            <VStack align="stretch" spacing={1}>
              {items.map(({ label, onClick, icon = FaFileExcel, colorScheme = "green" }) => (
                <Button
                  key={label}
                  leftIcon={<Icon as={icon} />}
                  size="sm"
                  variant="ghost"
                  justifyContent="flex-start"
                  width="100%"
                  colorScheme={colorScheme}
                  fontWeight="medium"
                  py={2}
                  borderRadius="md"
                  _hover={{ bg: `${colorScheme}.50`, _dark: { bg: `${colorScheme}.900` } }}
                  onClick={() => {
                    onClose()
                    onClick()
                  }}
                >
                  {label}
                </Button>
              ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  )
}

export default ExportMenu
